import { getErrorMessage, useConfirmation, useDeleteConfirmation, useFileViewer, useSection, useServices, useToast } from '@/app/providers';
import { DataTable, type ColumnDef } from '@/core/ui/components';
import { SectionSelector } from '@/modules/sections/ui/components/SectionSelector';
import { useAbility } from '@/modules/identity/application/ability';
import { usePaginatedList } from '@/shared/hooks';
import { useI18n } from '@/shared/i18n';
import { HouseholdMember, HouseholdMemberRelation, Unit, UnitType, User, UserRole } from '@/shared/types';
import type { QueryParams } from '@yawi/domain';
import { ApiError, capitalizeWords, getInitials } from '@yawi/infrastructure';
import { Ban, Building2, CheckCircle, Clock, Crown, Eye, Home, Loader, Mail, Phone, RotateCcw, Save, Search, Send, Trash2, User as UserIcon, UserPlus, Users, X } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../atoms/Button';
import { ResidentOnboardingWizard } from './ResidentOnboardingWizard';
import { Input } from '../atoms/Input';

// Helper: Format phone number as user types
const formatPhoneNumber = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};


interface EditingUserState extends Partial<User> {
  unitIds?: string[];
}

// Serializa los campos editables del modal para detectar cambios sin guardar
const buildEditSnapshot = (user: EditingUserState, units: Unit[]): string =>
  JSON.stringify({
    name: user.name || '',
    email: user.email || '',
    phone: user.phone || '',
    acsCode: user.acsCode ?? '',
    unitIds: units.map((unit) => unit.id).filter(Boolean).sort(),
  });

type UserWithUnitRefs = User & {
  unitId?: string | null;
  unit_id?: string | null;
  unitIds?: string[];
  unit_ids?: string[];
  tenantMember?: { unitId?: string | null; unit_id?: string | null } | null;
  tenant_member?: { unitId?: string | null; unit_id?: string | null } | null;
  member?: { unitId?: string | null; unit_id?: string | null } | null;
  memberships?: Array<{ unitId?: string | null; unit_id?: string | null }>;
  unitAssignments?: Array<{ unitId?: string | null; unit_id?: string | null }>;
  unit_assignments?: Array<{ unitId?: string | null; unit_id?: string | null }>;
};

type CommunityRole = 'RESIDENT' | 'FAMILIAR';

type ResidentFilters = {
  status?: string;
  principal?: '' | 'PRINCIPAL' | 'NO_PRINCIPAL';
  sectionId?: string;
};

type NewHouseholdMemberForm = {
  unitId: string;
  name: string;
  email: string;
  phone: string;
  relation: HouseholdMemberRelation;
};

const DEFAULT_HOUSEHOLD_MEMBER_FORM: NewHouseholdMemberForm = {
  unitId: '',
  name: '',
  email: '',
  phone: '',
  relation: 'OTHER',
};

const HOUSEHOLD_RELATION_OPTIONS: Array<{ value: HouseholdMemberRelation; label: string }> = [
  { value: 'SPOUSE', label: 'Conyuge' },
  { value: 'CHILD', label: 'Hijo/a' },
  { value: 'PARENT', label: 'Padre/Madre' },
  { value: 'OTHER', label: 'Otro' },
];

// Helper para mostrar nombre de unidad
const getUnitDisplayName = (unit: Unit): string => {
  if (unit.name) return unit.name;
  if (unit.type === UnitType.APARTMENT || unit.tower) {
    return `${unit.tower || ''} - ${unit.number || unit.floor || ''}`.trim();
  }
  return `${unit.street || ''} #${unit.exteriorNumber || unit.number || ''}`.trim();
};

// Helper para la ubicación (calle + número o torre) de una unidad puntual
const getUnitLocationLabel = (unit: Unit): string => {
  if (unit.street && unit.exteriorNumber) return `${unit.street} ${unit.exteriorNumber}`;
  if (unit.street) return unit.street;
  if (unit.tower) return `Torre ${unit.tower}`;
  return unit.name || '-';
};

// Ubicación de TODAS las unidades del usuario (un residente puede tener más de una
// casa, y ser principal de alguna en particular sin serlo de las demás).
const getUserLocationLines = (user: User): Array<{ label: string; isPrincipal: boolean }> => {
  if (user.units && user.units.length > 0) {
    return user.units.map((assignment) => ({
      label: getUnitLocationLabel(assignment.unit),
      isPrincipal: Boolean(assignment.isPrincipal),
    }));
  }
  if (user.residenceInfo) {
    const isPrincipal = Boolean(user.isPrincipal);
    if (user.residenceInfo.street && user.residenceInfo.unit) return [{ label: `${user.residenceInfo.street} ${user.residenceInfo.unit}`, isPrincipal }];
    if (user.residenceInfo.street) return [{ label: user.residenceInfo.street, isPrincipal }];
    if (user.residenceInfo.tower) return [{ label: `Torre ${user.residenceInfo.tower}`, isPrincipal }];
  }
  return [];
};


const getUnitDetailsDisplay = (unit: Unit): string => {
  if (unit.block) return `Manzana ${unit.block}`;
  if (unit.number) return `No. ${unit.number}`;
  if (unit.floor && unit.tower) return `Piso ${unit.floor}`;
  if (unit.exteriorNumber) return `No. ${unit.exteriorNumber}`;
  return unit.name || '-';
};

// Helper para mostrar las unidades de un usuario
const getUserUnitsDisplay = (user: User): string => {
  if (user.units && user.units.length > 0) {
    if (user.units.length === 1) {
      return getUnitDisplayName(user.units[0].unit);
    }
    const primary = user.units.find(u => u.isPrimary) || user.units[0];
    const otherCount = user.units.length - 1;
    return `${getUnitDisplayName(primary.unit)} (+${otherCount})`;
  }
  if (user.residenceInfo) {
    return `${user.residenceInfo.tower || ''} ${user.residenceInfo.unit}`.trim();
  }
  return '-';
};

const unitMatchesResidence = (unit: Unit, residenceInfo: User['residenceInfo']): boolean => {
  if (!residenceInfo) return false;
  const residenceUnit = residenceInfo.unit?.trim().toLowerCase();
  const residenceTower = residenceInfo.tower?.trim().toLowerCase();
  const residenceStreet = residenceInfo.street?.trim().toLowerCase();

  if (residenceTower) {
    return unit.tower?.trim().toLowerCase() === residenceTower
      && (unit.number?.trim().toLowerCase() === residenceUnit || unit.floor?.trim().toLowerCase() === residenceUnit);
  }

  if (residenceStreet) {
    return unit.street?.trim().toLowerCase() === residenceStreet
      && (unit.exteriorNumber?.trim().toLowerCase() === residenceUnit || unit.number?.trim().toLowerCase() === residenceUnit);
  }

  return unit.number?.trim().toLowerCase() === residenceUnit
    || unit.exteriorNumber?.trim().toLowerCase() === residenceUnit
    || unit.name?.trim().toLowerCase() === residenceUnit;
};

const userBelongsToUnit = (user: User, unit: Unit): boolean => {
  if (user.units?.some((assignment) => assignment.unitId === unit.id || assignment.unit?.id === unit.id)) {
    return true;
  }
  return unitMatchesResidence(unit, user.residenceInfo);
};

const getUserUnitIds = (user: User): string[] => {
  const rawUser = user as UserWithUnitRefs;
  const ids = [
    ...(user.units?.map((assignment) => assignment.unitId) || []),
    ...(rawUser.unitIds || []),
    ...(rawUser.unit_ids || []),
    rawUser.unitId,
    rawUser.unit_id,
    rawUser.tenantMember?.unitId,
    rawUser.tenantMember?.unit_id,
    rawUser.tenant_member?.unitId,
    rawUser.tenant_member?.unit_id,
    rawUser.member?.unitId,
    rawUser.member?.unit_id,
    rawUser.memberships?.[0]?.unitId,
    rawUser.memberships?.[0]?.unit_id,
    rawUser.unitAssignments?.[0]?.unitId,
    rawUser.unitAssignments?.[0]?.unit_id,
    rawUser.unit_assignments?.[0]?.unitId,
    rawUser.unit_assignments?.[0]?.unit_id,
  ].filter((id): id is string => Boolean(id));

  return [...new Set(ids)];
};

const getCommunityRole = (user: User): CommunityRole | null => {
  const role = String(user.tenantRole || user.role || '').toUpperCase();
  if (role === 'FAMILIAR') return 'FAMILIAR';
  if (role === 'RESIDENT') return 'RESIDENT';
  return null;
};

const getRateLimitSeconds = (error: unknown): number => {
  const errorDetails = typeof error === 'object' && error !== null && 'details' in error
    ? (error as { details?: Record<string, unknown> }).details
    : undefined;
  const candidates = [
    errorDetails?.retryAfter,
    errorDetails?.retryAfterSeconds,
    errorDetails?.seconds,
    errorDetails?.waitSeconds,
  ];

  for (const candidate of candidates) {
    const seconds = Number(candidate);
    if (Number.isFinite(seconds) && seconds > 0) {
      return Math.ceil(seconds);
    }
  }

  const message = error instanceof Error ? error.message : '';
  const messageSeconds = message.match(/(\d+)\s*(?:s|segundos|seconds)/i);
  if (messageSeconds?.[1]) {
    return Math.ceil(Number(messageSeconds[1]));
  }

  return 60;
};

const isRateLimitedResponse = (error: unknown): boolean => (
  typeof error === 'object'
  && error !== null
  && 'statusCode' in error
  && Number((error as { statusCode?: unknown }).statusCode) === 429
);

const isNotFoundResponse = (error: unknown): boolean => (
  typeof error === 'object'
  && error !== null
  && 'statusCode' in error
  && Number((error as { statusCode?: unknown }).statusCode) === 404
);

// El backend rechaza borrar al residente principal de una unidad con otros
// residentes con un mensaje largo y técnico ("...transfiere el rol principal
// primero"); se detecta por palabras clave y se muestra uno más simple.
const isPrincipalDeleteRejected = (error: unknown): boolean => {
  const message = getErrorMessage(error).toLowerCase();
  return message.includes('principal') && (message.includes('eliminar') || message.includes('transfier'));
};

// 404 "Miembro del hogar no encontrado en la unidad indicada": el id no es
// el de un household_member real (es un co-residente con cuenta propia) — no
// es lo mismo que "ya se había eliminado" y no debe tratarse como éxito.
const isHouseholdMemberNotResolvable = (error: unknown): boolean => (
  getErrorMessage(error).toLowerCase().includes('no encontrado en la unidad')
);

// USER_2005 manda details.units: una unidad o varias, si es principal de
// más de una. Arma el mensaje distinguiendo singular/plural.
const buildPrincipalRejectedMessage = (name: string, error: unknown): string => {
  const details = ApiError.isApiError(error) ? error.details : undefined;
  const rawUnits = details?.units;
  const unitNames: string[] = Array.isArray(rawUnits)
    ? rawUnits
      .map((unit) => (unit as { unitName?: string | null })?.unitName)
      .filter((unitName): unitName is string => !!unitName)
    : [];

  if (unitNames.length === 0) {
    return `"${name}" es el residente principal de su unidad. Designa otro residente como principal y luego intenta eliminarlo.`;
  }
  if (unitNames.length === 1) {
    return `"${name}" es el residente principal de la unidad "${unitNames[0]}". Designa otro residente como principal y luego intenta eliminarlo.`;
  }
  const quoted = unitNames.map((unitName) => `"${unitName}"`);
  const last = quoted.pop() ?? '';
  return `"${name}" es el residente principal de las unidades ${quoted.join(', ')} y ${last}. Designa otro residente como principal en cada una y luego intenta eliminarlo.`;
};

export const AdminResidentList = () => {
  const { t } = useI18n();
  const toast = useToast();
  const { confirmDelete } = useDeleteConfirmation();
  const { confirm } = useConfirmation();
  const { userRepo, unitRepo, startImpersonation } = useServices();
  const ability = useAbility();
  const navigate = useNavigate();
  // ?search= llega desde la búsqueda global del encabezado.
  const urlSearch = useSearchParams()[0].get('search');
  const { openFileByUrl } = useFileViewer();
  const { sections, selectedSectionId, hasSections } = useSection();
  const sectionNameById = useMemo(
    () => new Map(sections.map((section) => [section.id, section.name])),
    [sections],
  );
  const canUseResidentAction = useCallback((action: 'create' | 'update' | 'delete') => (
    ability.can(action, 'ResidentMenu')
  ), [ability]);
  const canCreateResident = canUseResidentAction('create');
  const canUpdateResident = canUseResidentAction('update');
  const canDeleteResident = canUseResidentAction('delete');
  // El backend valida la transferencia de principal con una ability separada
  // (MANAGE UnitPrincipal), distinta de editar/crear residentes.
  const canManagePrincipal = ability.can('manage', 'all') || ability.can('manage', 'UnitPrincipal');
  // "Ver como" es un permiso propio, independiente de poder editar residentes.
  const canImpersonate = ability.can('read', 'Impersonation');
  // Unidades con residentes activos pero sin principal (GET /units/principal-conflicts)
  const [conflictUnitIds, setConflictUnitIds] = useState<Set<string>>(new Set());

  const loadPrincipalConflicts = useCallback(async () => {
    try {
      const response = await unitRepo.getPrincipalConflicts({ page: 1, limit: 100 });
      setConflictUnitIds(new Set(response.data.map((conflict) => conflict.unitId)));
    } catch {
      // Informativo: si falla no bloquea el listado
      setConflictUnitIds(new Set());
    }
  }, [unitRepo]);

  useEffect(() => {
    void loadPrincipalConflicts();
  }, [loadPrincipalConflicts]);

  const fetchResidents = useCallback(
    async (params: QueryParams) => {
      // Todo el filtrado y la paginación ocurren en el backend
      // (WHERE -> COUNT -> ORDER BY -> LIMIT/OFFSET). Si se filtrara aquí,
      // `meta.total` describiría otra cosa que la página mostrada y los botones
      // de paginación quedarían mal numerados.
      const status = typeof params.status === 'string' ? params.status : '';
      const sectionId =
        typeof params.sectionId === 'string' && params.sectionId ? params.sectionId : undefined;
      const response = await userRepo.getUsers({
        ...params,
        search: typeof params.search === 'string' ? params.search.trim() : '',
        role: 'RESIDENT,FAMILIAR',
        status,
        ...(sectionId ? { sectionId } : {}),
      });

      return response;
    },
    [userRepo],
  );

  // Paginated list - filter only RESIDENT and FAMILIAR roles
  const {
    data: users,
    meta,
    loading,
    setPage,
    pageSize,
    setPageSize,
    search,
    setSearch,
    filters,
    setFilters,
    refresh,
  } = usePaginatedList<User, ResidentFilters>({
    fetchFn: fetchResidents,
    initialFilters: { status: '', principal: '', sectionId: selectedSectionId ?? '' },
    initialSearch: urlSearch ?? '',
    errorTitle: 'Error al cargar residentes',
  });

  // El titular (relatedTo) ya viene resuelto por el backend desde
  // tenant_members.related_to_member_id; aquí solo se arma la etiqueta de
  // unidad que se muestra cuando el familiar no trae unidades propias.
  // Se deriva de la página mostrada: una respuesta obsoleta no puede pisarla.
  const familyUnitLabels = useMemo(() => {
    const residentsById = new Map(
      users
        .filter((user) => getCommunityRole(user) === 'RESIDENT')
        .map((resident) => [resident.id, resident]),
    );
    const unitLabelMap: Record<string, string> = {};
    users.forEach((user) => {
      if (getCommunityRole(user) !== 'FAMILIAR') return;
      const owner = user.relatedTo ? residentsById.get(user.relatedTo.masterUserId) : undefined;
      const unitLabel = getUserUnitsDisplay(user);
      unitLabelMap[user.id] =
        unitLabel && unitLabel !== '-' ? unitLabel : owner ? getUserUnitsDisplay(owner) : '';
    });
    return unitLabelMap;
  }, [users]);

  // La lista sigue al selector de sección del contexto, también cuando este se
  // resuelve después del montaje (sección guardada al recargar). El `fetchFn`
  // no cae al contexto: el filtro explícito ('' = todas) siempre gana.
  useEffect(() => {
    const sectionId = selectedSectionId ?? '';
    if (filters.sectionId !== sectionId) setFilters({ sectionId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSectionId]);

  // Elegir otro resultado de la búsqueda global estando ya en esta página.
  useEffect(() => {
    if (urlSearch !== null) setSearch(urlSearch);
  }, [urlSearch, setSearch]);

  // Wizard State
  const [isWizardOpen, setIsWizardOpen] = useState(false);

  const handleWizardCompleted = (resident: User) => {
    setIsWizardOpen(false);
    refresh();
    void loadPrincipalConflicts();
    toast.success(`${resident.name} fue incorporado exitosamente`);
  };

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<EditingUserState>({});
  const editSnapshotRef = useRef<string | null>(null);
  const [householdMembers, setHouseholdMembers] = useState<HouseholdMember[]>([]);
  const [loadingHousehold, setLoadingHousehold] = useState(false);
  const [resendingMemberId, setResendingMemberId] = useState<string | null>(null);
  const [transferringPrincipalId, setTransferringPrincipalId] = useState<string | null>(null);
  const [resendCooldowns, setResendCooldowns] = useState<Record<string, number>>({});
  const [newHouseholdMember, setNewHouseholdMember] = useState<NewHouseholdMemberForm>(DEFAULT_HOUSEHOLD_MEMBER_FORM);
  const [creatingHouseholdMember, setCreatingHouseholdMember] = useState(false);

  // Unit search state
  const [unitSearch, setUnitSearch] = useState('');
  const [unitSearchResults, setUnitSearchResults] = useState<Unit[]>([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [showUnitDropdown, setShowUnitDropdown] = useState(false);
  const [selectedUnits, setSelectedUnits] = useState<Unit[]>([]);
  // Unidad activa en la sección "Residentes" cuando el usuario tiene más de una casa
  const [activeMemberUnitId, setActiveMemberUnitId] = useState<string>('');
  const unitDropdownRef = useRef<HTMLDivElement>(null);
  const editingCommunityRole = getCommunityRole(editingUser as User);
  const canManageHouseholdMembers = editingCommunityRole !== 'FAMILIAR';
  // El backend solo acepta al residente principal de la unidad como "receptor"
  // de un miembro del hogar nuevo — un residente no-principal no puede recibir
  // invitaciones aunque tenga permiso general de crear residentes.
  const addMemberTargetUnitId = newHouseholdMember.unitId || selectedUnits[0]?.id;
  const editingUserIsPrincipalOfTargetUnit = selectedUnits.length > 1
    ? (editingUser.units?.find((assignment) => assignment.unitId === addMemberTargetUnitId)?.isPrincipal ?? false)
    : (editingUser.isPrincipal ?? false);
  // El residente principal (is_principal en el backend) siempre se muestra primero
  const sortedHouseholdMembers = useMemo(
    () => [...householdMembers].sort((a, b) => Number(b.isPrincipal ?? false) - Number(a.isPrincipal ?? false)),
    [householdMembers],
  );
  // Con más de una unidad seleccionada, la tabla de residentes se filtra por
  // la pestaña de unidad activa; con una sola unidad se muestran todos.
  const visibleHouseholdMembers = useMemo(() => {
    if (selectedUnits.length <= 1) return sortedHouseholdMembers;
    return sortedHouseholdMembers.filter((member) => member.unitId === activeMemberUnitId);
  }, [activeMemberUnitId, selectedUnits.length, sortedHouseholdMembers]);

  useEffect(() => {
    if (Object.keys(resendCooldowns).length === 0) return;

    const timer = window.setInterval(() => {
      setResendCooldowns((current) => {
        const nextCooldowns: Record<string, number> = {};

        Object.entries(current).forEach(([memberId, seconds]) => {
          if (seconds > 1) {
            nextCooldowns[memberId] = seconds - 1;
          }
        });

        return nextCooldowns;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [resendCooldowns]);

  // Debounced unit search. `active` descarta la respuesta de un texto que ya
  // cambió: limpiar el timer no cancela una petición que ya salió.
  useEffect(() => {
    const term = unitSearch.trim();
    if (term.length < 2) {
      setUnitSearchResults([]);
      return;
    }

    let active = true;
    const timer = setTimeout(async () => {
      setLoadingUnits(true);
      try {
        const results = await unitRepo.getUnits({ search: term, limit: 10 });
        if (active) setUnitSearchResults(results.data);
      } catch (e) {
        console.error(e);
      } finally {
        if (active) setLoadingUnits(false);
      }
    }, 300);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [unitSearch, unitRepo]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (unitDropdownRef.current && !unitDropdownRef.current.contains(event.target as Node)) {
        setShowUnitDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const resolveUnitById = async (unitId: string): Promise<Unit | null> => {
    const unitRepoWithLookup = unitRepo as typeof unitRepo & {
      getUnitById?: (id: string) => Promise<Unit | null>;
    };

    if (unitRepoWithLookup.getUnitById) {
      try {
        const unit = await unitRepoWithLookup.getUnitById(unitId);
        if (unit) return unit;
      } catch (error) {
        console.error(error);
      }
    }

    try {
      const results = await unitRepo.getUnits({ page: 1, limit: 100, search: unitId });
      return results.data.find((unit) => unit.id === unitId) || null;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const dedupeUnits = (units: Unit[]): Unit[] => {
    const unitMap = new Map<string, Unit>();

    units.forEach((unit) => {
      const key = unit.id || getUnitDisplayName(unit);
      if (key && !unitMap.has(key)) {
        unitMap.set(key, unit);
      }
    });

    return [...unitMap.values()];
  };

  const resolveAssignedUnitsForUser = async (userToResolve: User): Promise<Unit[]> => {
    const userUnitIds = getUserUnitIds(userToResolve);
    let resolvedUnits = userToResolve.units?.length
      ? userToResolve.units.map((assignment) => assignment.unit).filter(Boolean)
      : [];

    const missingUnitIds = userUnitIds.filter((unitId) => !resolvedUnits.some((unit) => unit.id === unitId));
    if (missingUnitIds.length > 0) {
      const unitsFromIds = await Promise.all(missingUnitIds.map(async (unitId) => {
        const resolvedUnit = await resolveUnitById(unitId);
        return resolvedUnit || ({
          id: unitId,
          type: UnitType.HOUSE,
          name: `Unidad asignada (${unitId.slice(0, 8)})`,
        } as Unit);
      }));
      resolvedUnits = [...resolvedUnits, ...unitsFromIds];
    }

    if (resolvedUnits.length === 0 && userToResolve.residenceInfo) {
      const terms = [
        [userToResolve.residenceInfo.tower, userToResolve.residenceInfo.unit].filter(Boolean).join(' '),
        [userToResolve.residenceInfo.street, userToResolve.residenceInfo.unit].filter(Boolean).join(' '),
        userToResolve.residenceInfo.unit,
      ].filter(Boolean);

      for (const term of terms) {
        try {
          const result = await unitRepo.getUnits({ search: term, limit: 20 });
          const matchedUnit = result.data.find((unit) => unitMatchesResidence(unit, userToResolve.residenceInfo));
          if (matchedUnit) {
            resolvedUnits = [matchedUnit];
            break;
          }
        } catch (error) {
          console.error(error);
          break;
        }
      }
    }

    if (resolvedUnits.length === 0) {
      try {
        const [unitsPage, residentsPage] = await Promise.all([
          unitRepo.getUnits({ page: 1, limit: 100 }),
          userRepo.getUsers({ page: 1, limit: 100, role: 'RESIDENT' }),
        ]);
        const richerResident = residentsPage.data.find((resident) => resident.id === userToResolve.id);
        const candidate = richerResident
          ? {
              ...userToResolve,
              ...richerResident,
              units: richerResident.units?.length ? richerResident.units : userToResolve.units,
              residenceInfo: richerResident.residenceInfo || userToResolve.residenceInfo,
            }
          : userToResolve;
        const assignmentUnits = candidate.units?.length
          ? candidate.units.map((assignment) => assignment.unit).filter(Boolean)
          : [];
        const matchedUnits = unitsPage.data.filter((unit) => userBelongsToUnit(candidate, unit));

        resolvedUnits = [...assignmentUnits, ...matchedUnits];
      } catch (error) {
        console.error(error);
      }
    }

    return dedupeUnits(resolvedUnits);
  };

  const loadHouseholdMembersForUnits = async (units: Unit[]) => {
    const unitsWithId = units.filter((unit) => unit.id);
    if (unitsWithId.length === 0) {
      setHouseholdMembers([]);
      return;
    }

    setLoadingHousehold(true);
    try {
      const results = await Promise.all(
        unitsWithId.map(async (unit) => ({
          unitId: unit.id,
          members: await userRepo.getUnitHouseholdMembers(unit.id),
        })),
      );

      // Un residente puede pertenecer a más de una unidad seleccionada, y cada
      // membresía es una fila distinta (puede ser principal en una y no en
      // otra) — NO deduplicar por member.id entre unidades o desaparece de
      // todas menos la última procesada.
      const members = results.flatMap(({ unitId, members: unitMembers }) => (
        unitMembers.map((member) => ({ ...member, unitId: member.unitId || unitId }))
      ));

      setHouseholdMembers(members);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudieron cargar los residentes de la unidad';
      toast.error(message);
      setHouseholdMembers([]);
    } finally {
      setLoadingHousehold(false);
    }
  };

  const handleDeleteHouseholdMember = async (member: HouseholdMember) => {
    if (!canDeleteResident) return;
    const unitId = member.unitId || selectedUnits[0]?.id;
    if (!unitId) return;

    // Orden importa: solo alguien con cuenta propia (editable === false)
    // puede ser principal, así que si se revisa "cuenta propia" primero, el
    // caso de principal (más específico y accionable: "transfiere el rol")
    // nunca se llega a mostrar. Se revisa principal primero.
    if (member.isPrincipal) {
      const hasOtherResidents = householdMembers.some(
        (other) => other.unitId === unitId && other.id !== member.id
      );
      if (hasOtherResidents) {
        const unit = selectedUnits.find((candidate) => candidate.id === unitId);
        const unitName = unit ? getUnitDisplayName(unit) : null;
        const unitPhrase = unitName ? `la unidad "${unitName}"` : 'su unidad';
        toast.error(
          `"${member.name}" es el residente principal de ${unitPhrase}. Designa otro residente como principal y luego intenta eliminarlo.`,
          'Error al eliminar residente'
        );
        return;
      }
    }

    // member.editable === false: esta fila es un co-residente con cuenta
    // propia (tenant_member), no un household_member simple — el backend
    // responde 404 "no encontrado" porque ese id no existe para este
    // endpoint, no porque ya se haya borrado. Se bloquea antes de llamar al
    // backend para no confundir ese 404 con "ya estaba eliminado".
    if (member.editable === false) {
      toast.error(
        `"${member.name}" tiene cuenta propia en esta unidad y no se puede eliminar desde aquí. Elimínalo desde la lista principal de Residentes.`,
        'Error al eliminar residente'
      );
      return;
    }

    await confirmDelete({
      itemName: member.name,
      itemType: 'Residente',
      onConfirm: async () => {
        try {
          await userRepo.deleteUnitHouseholdMember(unitId, member.id);
          toast.success(`${member.name} fue eliminado de la unidad`);
        } catch (error) {
          // USER_2005: es el residente principal de una o más unidades con
          // otros residentes. El backend manda los nombres en details; si por
          // alguna razón no viene (respuesta vieja), se cae al nombre de la
          // unidad activa en el modal como mejor intento, y a
          // isPrincipalDeleteRejected (por texto del mensaje) si ni el code viene.
          const isApiPrincipalRejection = ApiError.isApiError(error) && error.code === 'USER_2005';
          if (isApiPrincipalRejection || isPrincipalDeleteRejected(error)) {
            const hasUnitDetails = isApiPrincipalRejection
              && ApiError.isApiError(error)
              && !!(error.details?.units as unknown[] | undefined)?.length;
            const message = hasUnitDetails
              ? buildPrincipalRejectedMessage(member.name, error)
              : (() => {
                const fallbackUnit = selectedUnits.find((candidate) => candidate.id === unitId);
                const unitName = fallbackUnit ? getUnitDisplayName(fallbackUnit) : null;
                const unitPhrase = unitName ? `la unidad "${unitName}"` : 'su unidad';
                return `"${member.name}" es el residente principal de ${unitPhrase}. Designa otro residente como principal y luego intenta eliminarlo.`;
              })();
            toast.error(message, 'Error al eliminar residente');
            throw error;
          }
          if (isHouseholdMemberNotResolvable(error)) {
            toast.error(
              `"${member.name}" tiene cuenta propia en esta unidad y no se puede eliminar desde aquí. Elimínalo desde la lista principal de Residentes.`,
              'Error al eliminar residente'
            );
            throw error;
          }
          // Ya no existe en el backend (p. ej. un intento previo sí lo borró
          // pero la UI no se actualizó): igual lo quitamos de la lista local
          // en vez de dejarlo "atascado" para que el usuario lo reintente.
          if (isNotFoundResponse(error)) {
            toast.info(`${member.name} ya había sido eliminado`);
          } else {
            toast.error(getErrorMessage(error), 'Error al eliminar residente');
            throw error;
          }
        }
        // Filtrar por id Y unidad: si el residente tiene otra unidad
        // seleccionada, su membresía en esa otra unidad debe permanecer.
        setHouseholdMembers((current) => (
          current.filter((item) => !(item.id === member.id && item.unitId === unitId))
        ));
        refresh();

        // member.id/isPrincipal no son un indicador confiable de si esta fila
        // es el mismo usuario que está abierto en el modal (HouseholdMember y
        // User son entidades distintas en el backend) — se verifica contra el
        // backend en vez de adivinar. Si ya no existe, no tiene sentido seguir
        // mostrando el formulario de edición ni dejar que aprieten Guardar
        // contra un usuario borrado.
        if (editingUser.id) {
          const stillExists = await userRepo.getUserById(editingUser.id);
          if (!stillExists) {
            setIsModalOpen(false);
          }
        }
      },
    });
  };

  const handleMakePrincipal = async (member: HouseholdMember) => {
    if (!canManagePrincipal) return;
    const unitId = member.unitId || selectedUnits[0]?.id;
    if (!unitId) return;

    const confirmed = await confirm({
      title: 'Cambiar residente principal',
      message: `${member.name} pasará a ser el residente principal de la unidad. El principal actual dejará de serlo.`,
      confirmText: 'Hacer principal',
      cancelText: 'Cancelar',
      variant: 'info',
    });
    if (!confirmed) return;

    setTransferringPrincipalId(member.id);
    try {
      await userRepo.transferUnitPrincipal(unitId, member.id);
      // El backend garantiza un solo principal por unidad: reflejamos lo mismo
      // localmente, sin tocar miembros de otras unidades seleccionadas.
      setHouseholdMembers((current) =>
        current.map((item) => {
          if (item.unitId !== unitId) return item;
          return { ...item, isPrincipal: item.id === member.id };
        }),
      );
      toast.success(`${member.name} ahora es el residente principal`);
      // Refleja el cambio en la tabla principal (coronas y badges de conflicto)
      refresh();
      void loadPrincipalConflicts();
    } catch (error) {
      toast.error(getErrorMessage(error), 'Error al cambiar el residente principal');
    } finally {
      setTransferringPrincipalId(null);
    }
  };

  const handleResendInvitation = async (member: HouseholdMember) => {
    const currentCooldown = resendCooldowns[member.id] || 0;
    if (currentCooldown > 0) {
      return;
    }

    const unitId = member.unitId || selectedUnits[0]?.id;
    if (!unitId) {
      return;
    }

    setResendingMemberId(member.id);
    try {
      await userRepo.resendUnitHouseholdMemberInvitation(unitId, member.id);
      setResendCooldowns((current) => ({ ...current, [member.id]: 60 }));
      toast.success(`Invitacion reenviada a ${member.email}`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudo reenviar la invitacion.';
      if (isRateLimitedResponse(error)) {
        const seconds = getRateLimitSeconds(error);
        setResendCooldowns((current) => ({ ...current, [member.id]: seconds }));
      } else {
        toast.error(message);
      }
    } finally {
      setResendingMemberId(null);
    }
  };

  const handleCreateHouseholdMember = async () => {
    if (!canCreateResident) return;
    if (!editingUserIsPrincipalOfTargetUnit) {
      toast.warning('Solo el residente principal de la unidad puede recibir nuevos miembros del hogar.');
      return;
    }
    const unitId = newHouseholdMember.unitId || selectedUnits[0]?.id;
    if (!editingUser.id || !unitId) {
      toast.warning('Selecciona una unidad para agregar el residente.');
      return;
    }
    if (!newHouseholdMember.name.trim() || !newHouseholdMember.email.trim()) {
      toast.warning('Nombre y correo son obligatorios.');
      return;
    }

    setCreatingHouseholdMember(true);
    try {
      const member = await userRepo.createUnitHouseholdMember(unitId, editingUser.id, {
        name: newHouseholdMember.name.trim(),
        email: newHouseholdMember.email.trim(),
        phone: newHouseholdMember.phone.replace(/\D/g, ''),
        relation: newHouseholdMember.relation,
      });
      const memberWithUnit: HouseholdMember = { ...member, unitId: member.unitId || unitId };
      setHouseholdMembers((current) => (
        current.some((item) => item.unitId === unitId && (item.id === member.id || item.email === member.email))
          ? current
          : [...current, memberWithUnit]
      ));
      setNewHouseholdMember({ ...DEFAULT_HOUSEHOLD_MEMBER_FORM, unitId });
      toast.success(`Invitacion enviada a ${member.email}`);
      refresh();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudo agregar el residente.';
      toast.error(message);
    } finally {
      setCreatingHouseholdMember(false);
    }
  };

  const handleOpenEdit = async (user: User) => {
    if (!canUpdateResident) return;

    const detailedUser = await userRepo.getUserById(user.id);
    const userToEdit = detailedUser
      ? {
          ...user,
          ...detailedUser,
          units: detailedUser.units?.length ? detailedUser.units : user.units,
          residenceInfo: detailedUser.residenceInfo || user.residenceInfo,
          householdMembers: detailedUser.householdMembers || user.householdMembers,
        }
      : user;
    const userUnitIds = getUserUnitIds(userToEdit);
    const initialSelectedUnits = await resolveAssignedUnitsForUser(userToEdit);
    const initialSelectedUnitIds = initialSelectedUnits.map((unit) => unit.id).filter(Boolean);

    const nextEditingUser: EditingUserState = {
      ...userToEdit,
      role: UserRole.RESIDENT,
      unitIds: initialSelectedUnitIds.length > 0 ? initialSelectedUnitIds : userUnitIds,
    };
    setEditingUser(nextEditingUser);
    editSnapshotRef.current = buildEditSnapshot(nextEditingUser, initialSelectedUnits);
    setUnitSearch('');
    setUnitSearchResults([]);

    if (initialSelectedUnits.length > 0) {
      setSelectedUnits(initialSelectedUnits);
      setActiveMemberUnitId(initialSelectedUnits[0].id);
      setNewHouseholdMember({ ...DEFAULT_HOUSEHOLD_MEMBER_FORM, unitId: initialSelectedUnits[0].id });
    } else if (userToEdit.residenceInfo) {
      const displayName = userToEdit.residenceInfo.tower
        ? `${userToEdit.residenceInfo.tower} - ${userToEdit.residenceInfo.unit}`
        : `${userToEdit.residenceInfo.street || ''} #${userToEdit.residenceInfo.unit}`;
      setSelectedUnits([{
        id: '',
        type: userToEdit.residenceInfo.tower ? UnitType.APARTMENT : UnitType.HOUSE,
        tower: userToEdit.residenceInfo.tower,
        number: userToEdit.residenceInfo.unit,
        street: userToEdit.residenceInfo.street,
        name: displayName
      } as Unit]);
      setActiveMemberUnitId('');
      setNewHouseholdMember(DEFAULT_HOUSEHOLD_MEMBER_FORM);
    } else {
      setSelectedUnits([]);
      setActiveMemberUnitId('');
      setNewHouseholdMember(DEFAULT_HOUSEHOLD_MEMBER_FORM);
    }
    await loadHouseholdMembersForUnits(initialSelectedUnits);
    setShowUnitDropdown(false);
    setIsModalOpen(true);
  };

  const hasUnsavedChanges = () => {
    if (editSnapshotRef.current === null) return false;
    if (buildEditSnapshot(editingUser, selectedUnits) !== editSnapshotRef.current) return true;
    return Boolean(
      newHouseholdMember.name.trim() || newHouseholdMember.email.trim() || newHouseholdMember.phone.trim()
    );
  };

  const handleRequestCloseModal = async () => {
    if (hasUnsavedChanges()) {
      const confirmed = await confirm({
        title: 'Cambios sin guardar',
        message: 'Tienes información escrita que se perderá si cierras. ¿Seguro que deseas cerrar sin guardar?',
        confirmText: 'Cerrar sin guardar',
        cancelText: 'Seguir editando',
        variant: 'warning',
      });
      if (!confirmed) return;
    }
    setIsModalOpen(false);
  };

  const handleDelete = async (id: string, name: string) => {
    if (!canDeleteResident) return;

    await confirmDelete({
      itemName: name,
      itemType: 'Residente',
      onConfirm: async () => {
        try {
          await userRepo.deleteUser(id);
          toast.success('Residente eliminado correctamente');
          refresh();
          // Se borró al mismo residente que está abierto en el modal (p. ej.
          // se disparó desde el botón de eliminar de adentro del modal): no
          // tiene sentido dejarlo abierto mostrando datos de una cuenta que
          // ya no existe.
          if (id === editingUser.id) {
            setIsModalOpen(false);
          }
        } catch (error) {
          // USER_2005: es el residente principal de una o más unidades con
          // otros residentes. El backend manda los nombres en details.
          if (ApiError.isApiError(error) && error.code === 'USER_2005') {
            toast.error(buildPrincipalRejectedMessage(name, error), 'Error al eliminar residente');
          } else {
            toast.error(getErrorMessage(error), 'Error al eliminar residente');
          }
          throw error;
        }
      }
    });
  };

  const handleViewAs = async (user: User) => {
    try {
      await startImpersonation(user.id);
      navigate('/');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleRestore = async (user: User) => {
    if (!canUpdateResident) return;

    try {
      const restoredUser = await userRepo.restoreUser(user.id);
      toast.success(`${user.name} fue reactivado correctamente`);
      refresh();

      // La cuenta se reactiva bloqueada (sin password utilizable): se manda
      // magic link para que el residente entre y asigne su nueva contraseña.
      const email = restoredUser.email || user.email;
      if (email) {
        try {
          await userRepo.requestMagicLink(email);
          toast.info(`Se envió un enlace de acceso a ${email}`);
        } catch (magicLinkError) {
          toast.error(getErrorMessage(magicLinkError), 'No se pudo enviar el enlace de acceso');
        }
      }
    } catch (error) {
      toast.error(getErrorMessage(error), 'Error al reactivar');
    }
  };

  const handleToggleBlock = async (user: User) => {
    if (!canUpdateResident) return;

    const willBlock = user.status === 'ACTIVE';
    const confirmed = await confirm({
      title: willBlock ? 'Bloquear residente' : 'Desbloquear residente',
      message: willBlock
        ? `${user.name} no podrá acceder al sistema mientras esté bloqueado. ¿Deseas continuar?`
        : `${user.name} podrá volver a acceder al sistema. ¿Deseas continuar?`,
      confirmText: willBlock ? 'Bloquear' : 'Desbloquear',
      variant: willBlock ? 'warning' : 'info',
    });
    if (!confirmed) return;

    try {
      // Endpoints dedicados (no el PUT genérico): blockUser revoca tokens y
      // unblockUser reactiva la membresía del tenant, sin lo cual el usuario
      // queda con status ACTIVE pero sigue sin poder iniciar sesión.
      if (willBlock) {
        await userRepo.blockUser(user.id);
      } else {
        await userRepo.unblockUser(user.id);
      }
      toast.success(`Residente ${willBlock ? 'bloqueado' : 'desbloqueado'} correctamente`);
      refresh();
    } catch (error) {
      toast.error(getErrorMessage(error), willBlock ? 'Error al bloquear' : 'Error al desbloquear');
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser.name || !editingUser.email) return;
    const selectedUnitIds = selectedUnits.map((unit) => unit.id).filter(Boolean);
    const hasUnresolvedSelectedUnit = selectedUnits.some((unit) => !unit.id);

    try {
      const {
        unitIds,
        acsCode,
        residenceInfo,
        units,
        id,
        tenantRole,
        createdAt,
        updatedAt,
        pin,
        ...userData
      } = editingUser;
      const payload = { ...userData } as Record<string, unknown>;

      // Scrub phone to digits only before sending
      if (payload.phone) {
        payload.phone = (payload.phone as string).replace(/\D/g, '');
      }

      // acsCode viene destructurado de editingUser pero debe incluirse en el payload
      const rawEditing = editingUser as unknown as Record<string, unknown>;
      if ('acsCode' in rawEditing) {
        payload.acsCode = rawEditing.acsCode;
      }

      if (canManageHouseholdMembers && !hasUnresolvedSelectedUnit) {
        payload.unitIds = selectedUnitIds;
      }

      delete payload.password;
      delete payload.role;

      try {
        await userRepo.updateUser({ id: editingUser.id, ...payload } as unknown as User);
        toast.success('Residente actualizado correctamente');
      } catch (error) {
        // El usuario que estaba abierto ya no existe (p. ej. se eliminó desde
        // la lista de residentes de este mismo modal): no tiene sentido
        // mostrar un error de guardado por algo que el propio admin acaba de
        // hacer a propósito — solo cerramos y refrescamos en silencio.
        if (isNotFoundResponse(error)) {
          setIsModalOpen(false);
          refresh();
          return;
        }
        toast.error(getErrorMessage(error), 'Error al actualizar residente');
        return;
      }
      setIsModalOpen(false);
      refresh();
    } catch {
      // Error ya mostrado arriba
    }
  };

  const handleUnitSelect = (unit: Unit) => {
    const newSelectedUnits = selectedUnits.some((selectedUnit) => selectedUnit.id === unit.id)
      ? selectedUnits
      : [...selectedUnits, unit];
    setEditingUser({
      ...editingUser,
      unitIds: newSelectedUnits.map((selectedUnit) => selectedUnit.id)
    });
    setSelectedUnits(newSelectedUnits);
    // Al agregar una unidad nueva, mostrar de una vez sus residentes (vacío hoy).
    setActiveMemberUnitId(unit.id);
    setNewHouseholdMember((current) => ({
      ...current,
      unitId: unit.id,
    }));
    void loadHouseholdMembersForUnits(newSelectedUnits);
    setUnitSearch('');
    setShowUnitDropdown(false);
  };

  const handleUnitRemove = (unitId: string) => {
    const newSelectedUnits = selectedUnits.filter(u => u.id !== unitId);
    setSelectedUnits(newSelectedUnits);
    setEditingUser({
      ...editingUser,
      unitIds: newSelectedUnits.map(u => u.id)
    });
    setActiveMemberUnitId((current) => (
      current === unitId ? (newSelectedUnits[0]?.id || '') : current
    ));
    setNewHouseholdMember((current) => ({
      ...current,
      unitId: newSelectedUnits.some((unit) => unit.id === current.unitId) ? current.unitId : (newSelectedUnits[0]?.id || ''),
    }));
    void loadHouseholdMembersForUnits(newSelectedUnits);
  };

  const handleSwitchMemberUnit = (unitId: string) => {
    setActiveMemberUnitId(unitId);
    setNewHouseholdMember((current) => ({ ...current, unitId }));
  };

  // Column definitions
  const columns: ColumnDef<User>[] = useMemo(() => [
    {
      id: 'name',
      header: t('admin.users.table.name'),
      cell: (user) => (
        <div className="flex items-center gap-3">
          {user.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt={user.name}
              className={`w-8 h-8 rounded-full object-cover border-2 flex-shrink-0 cursor-pointer ${
                user.status === 'BLOCKED' ? 'border-red-300 opacity-70' : 'border-slate-200'
              }`}
              onClick={(e) => {
                e.stopPropagation();
                openFileByUrl(user.avatarUrl!, `${user.name}.jpg`, 'image/jpeg');
              }}
            />
          ) : (
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-slate-500 flex-shrink-0 ${
              user.status === 'BLOCKED' ? 'bg-red-100' : 'bg-slate-200'
            }`}>
              <span className="text-xs font-semibold text-slate-600">{getInitials(user.name)}</span>
            </div>
          )}
          <div>
            <p className={`font-medium ${user.status === 'BLOCKED' ? 'text-red-800 line-through' : 'text-slate-900'}`}>{capitalizeWords(user.name)}</p>
            <p className="text-xs text-slate-500">{user.email}</p>
          </div>
        </div>
      ),
    },
    {
      id: 'location',
      header: 'Ubicación',
      cell: (user) => {
        const role = getCommunityRole(user);
        const locations = getUserLocationLines(user);
        const fallbackUnit = role === 'FAMILIAR' ? familyUnitLabels[user.id] : '';

        if (locations.length === 0) {
          return <span className="text-slate-800">{fallbackUnit ? capitalizeWords(fallbackUnit) : '-'}</span>;
        }

        // La sección del residente se DERIVA de sus unidades, no se almacena.
        // Un residente puede tener unidades en dos secciones y entonces ve
        // ambas; si ninguna tiene sección, solo ve el contenido global.
        const sectionNames = [
          ...new Set(
            (user.units ?? [])
              .map((assignment) => assignment.unit?.sectionId)
              .filter((id): id is string => Boolean(id))
              .map((id) => sectionNameById.get(id))
              .filter((name): name is string => Boolean(name)),
          ),
        ];

        return (
          <div className="space-y-0.5">
            {locations.map((location, index) => (
              <div key={`${user.id}-loc-${index}`} className="flex items-center gap-1 text-slate-800">
                <span>{capitalizeWords(location.label)}</span>
                {location.isPrincipal && (
                  <span title="Principal de esta unidad">
                    <Crown size={11} className="fill-primary-400 text-primary-500 shrink-0" />
                  </span>
                )}
              </div>
            ))}
            {hasSections &&
              (sectionNames.length > 0 ? (
                <div className="text-xs text-slate-500">{sectionNames.join(' • ')}</div>
              ) : (
                <span className="inline-flex items-center rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                  Sin sección
                </span>
              ))}
          </div>
        );
      },
    },
    {
      id: 'phone',
      header: 'Teléfono',
      cell: (user) => (
        <span className="text-slate-600 text-xs">{user.phone ? formatPhoneNumber(user.phone) : '-'}</span>
      ),
    },
    {
      id: 'principal',
      header: 'Principal',
      className: 'text-center',
      cell: (user) => {
        if (user.isPrincipal) {
          return (
            <span className="inline-flex items-center gap-1.5 text-xs font-bold text-primary-600 whitespace-nowrap">
              <Crown size={15} className="fill-primary-400 text-primary-500" />
              Principal
            </span>
          );
        }
        const unitWithoutPrincipal = getUserUnitIds(user).some((unitId) => conflictUnitIds.has(unitId));
        if (unitWithoutPrincipal) {
          return (
            <span
              className="inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold bg-amber-50 text-amber-700 border-amber-200 whitespace-nowrap"
              title="Su unidad no tiene residente principal: asigna uno desde el modal de edición"
            >
              Unidad sin principal
            </span>
          );
        }
        return <span className="text-slate-300">—</span>;
      },
    },
    {
      id: 'status',
      header: t('admin.users.table.status'),
      className: 'text-center',
      cell: (user) => (
        user.status === 'DELETED' ? (
          <span className="inline-flex items-center gap-1 text-slate-500 text-xs font-bold bg-slate-100 px-2 py-1 rounded-full border border-slate-200">
            <Trash2 size={10} /> Eliminado
          </span>
        ) : user.status === 'ACTIVE' ? (
          <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-bold bg-emerald-50 px-2 py-1 rounded-full border border-emerald-100">
            <CheckCircle size={10} /> {t('admin.users.status.active')}
          </span>
        ) : user.status === 'PENDING_VERIFICATION' ? (
          <span className="inline-flex items-center gap-1 text-amber-600 text-xs font-bold bg-amber-50 px-2 py-1 rounded-full border border-amber-100">
            <Clock size={10} /> {t('admin.users.status.pending')}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-red-600 text-xs font-bold bg-red-50 px-2 py-1 rounded-full border border-red-100">
            <Ban size={10} /> {t('admin.users.status.blocked')}
          </span>
        )
      ),
    },
  ], [conflictUnitIds, familyUnitLabels, t, hasSections, sectionNameById]);

  return (
    <>
      <DataTable
        data={users}
        columns={columns}
        keyExtractor={(user) => user.id}
        meta={meta}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        title={t('admin.card.residents')}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por nombre, correo, unidad o direccion..."
        filters={
          <div className="flex flex-col sm:flex-row gap-2">
            <SectionSelector
              variant="slate"
              className="w-full sm:w-48"
              onSelectionChange={(id) => setFilters({ sectionId: id ?? '' })}
            />
            <select
              value={filters.status || ''}
              onChange={(event) => setFilters({ status: event.target.value })}
              className="h-[38px] min-w-44 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-100"
            >
              <option value="">Todos los estatus</option>
              <option value="ACTIVE">Activos</option>
              <option value="PENDING_VERIFICATION">Pendientes</option>
              <option value="BLOCKED">Bloqueados</option>
              <option value="DELETED">Eliminados</option>
            </select>
            <select
              value={filters.principal || ''}
              onChange={(event) => setFilters({ principal: event.target.value as ResidentFilters['principal'] })}
              className="h-[38px] min-w-44 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-100"
            >
              <option value="">Todos los residentes</option>
              <option value="PRINCIPAL">Solo principales</option>
              <option value="NO_PRINCIPAL">Unidades sin principal</option>
            </select>
          </div>
        }
        loading={loading}
        emptyMessage="No se encontraron residentes."
        emptyIcon={(
          <div className="w-16 h-16 rounded-full bg-primary-50 flex items-center justify-center mb-4">
            <Users className="w-8 h-8 text-primary-400" />
          </div>
        )}
        actions={canCreateResident ? (
          <Button onClick={() => setIsWizardOpen(true)} icon={<UserPlus size={16}/>}>
            Nuevo residente
          </Button>
        ) : undefined}
        rowActions={(user) => (
          user.status === 'DELETED' ? (
            canUpdateResident && (
              <button
                onClick={() => handleRestore(user)}
                className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                title="Reactivar"
              >
                <RotateCcw size={16} />
              </button>
            )
          ) : (
            <>
              {canImpersonate && (
                <button
                  onClick={() => handleViewAs(user)}
                  className="p-1.5 text-slate-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                  title={t('admin.users.viewAs')}
                >
                  <Eye size={16} />
                </button>
              )}
              {canUpdateResident && (
                <>
                  {user.status !== 'PENDING_VERIFICATION' && (
                    <button
                      onClick={() => handleToggleBlock(user)}
                      className={`p-1.5 rounded transition-colors ${user.status === 'ACTIVE' ? 'text-slate-400 hover:text-red-500 hover:bg-red-50' : 'text-emerald-500 hover:bg-emerald-50'}`}
                      title={user.status === 'ACTIVE' ? t('admin.users.modal.block') : t('admin.users.modal.unblock')}
                    >
                      {user.status === 'ACTIVE' ? <Ban size={16}/> : <CheckCircle size={16}/>}
                    </button>
                  )}
                  <button
                    onClick={() => handleOpenEdit(user)}
                    className="p-1.5 text-slate-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                    title={t('common.edit')}
                  >
                    <UserIcon size={16} />
                  </button>
                </>
              )}
              {canDeleteResident && (
                <button
                  onClick={() => handleDelete(user.id, user.name)}
                  className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  title={t('common.delete')}
                >
                  <Trash2 size={16} />
                </button>
              )}
            </>
          )
        )}
      />

      {/* Onboarding Wizard */}
      {isWizardOpen && (
        <ResidentOnboardingWizard
          onClose={() => setIsWizardOpen(false)}
          onCompleted={handleWizardCompleted}
        />
      )}

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center p-5 border-b border-slate-100">
              <h3 className="font-bold text-slate-800 text-lg">
                Editar Residente
              </h3>
              <button onClick={() => void handleRequestCloseModal()} className="text-slate-400 hover:text-slate-600">
                <X size={20}/>
              </button>
            </div>

            <div className="p-6 space-y-5 overflow-y-auto">
            <form id="resident-edit-form" onSubmit={handleSave} className="space-y-4">
              <Input
                label={t('admin.users.table.name')}
                value={editingUser.name || ''}
                onChange={e => setEditingUser({...editingUser, name: e.target.value})}
                required
              />
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label={t('common.email')}
                  type="email"
                  value={editingUser.email || ''}
                  onChange={e => setEditingUser({...editingUser, email: e.target.value})}
                  required
                />
                <Input
                  label={t('common.phone')}
                  value={editingUser.phone || ''}
                  onChange={e => setEditingUser({...editingUser, phone: formatPhoneNumber(e.target.value)})}
                  placeholder="(000) 000-0000"
                />
              </div>

              {/* ACS Code — enlace con HikCentral (solo en edición) */}
              {editingUser.id && (
                <div>
                  <Input
                    label="Código ACS (HikCentral)"
                    value={editingUser.acsCode ?? ''}
                    onChange={e => {
                      const digits = e.target.value.replace(/\D/g, '').slice(0, 32);
                      setEditingUser({ ...editingUser, acsCode: digits });
                    }}
                    placeholder="Ej: 0000085"
                    inputMode="numeric"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Solo dígitos. Los ceros a la izquierda importan. Déjalo vacío para desvincular.
                  </p>
                </div>
              )}

              {/* Unit Selector */}
              {canManageHouseholdMembers && (
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <label className="block text-sm font-bold text-slate-700 mb-2 flex items-center gap-2">
                  <Home size={16}/> {t('admin.units.unit_select')}
                  {selectedUnits.length > 0 && (
                    <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full">
                      {selectedUnits.length}
                    </span>
                  )}
                </label>

                {/* Selected units display */}
                {selectedUnits.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {selectedUnits.map((unit) => (
                      <div
                        key={unit.id}
                        className="flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-300 rounded-lg"
                      >
                        <Building2 size={14} className="text-slate-400" />
                        <span className="text-sm font-medium text-slate-700">
                          {getUnitDisplayName(unit)}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleUnitRemove(unit.id)}
                          className="text-slate-400 hover:text-red-500 ml-1"
                        >
                          <X size={14}/>
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="relative" ref={unitDropdownRef}>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder={selectedUnits.length > 0 ? "Agregar otra unidad..." : "Buscar por torre, número, calle..."}
                      value={unitSearch}
                      onChange={e => {
                        setUnitSearch(e.target.value);
                        setShowUnitDropdown(true);
                      }}
                      onFocus={() => setShowUnitDropdown(true)}
                      className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-100"
                    />
                    {loadingUnits ? (
                      <Loader size={16} className="absolute left-3 top-3 text-slate-400 animate-spin"/>
                    ) : (
                      <Search size={16} className="absolute left-3 top-3 text-slate-400"/>
                    )}
                  </div>

                  {/* Dropdown results */}
                  {showUnitDropdown && unitSearch.length >= 2 && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {loadingUnits ? (
                        <div className="p-3 text-center text-sm text-slate-500">
                          <Loader size={16} className="inline-block animate-spin mr-2"/>
                          Buscando...
                        </div>
                      ) : unitSearchResults.length > 0 ? (
                        unitSearchResults.map(unit => {
                          const isSelected = selectedUnits.some(u => u.id === unit.id);
                          return (
                            <button
                              key={unit.id}
                              type="button"
                              onClick={() => handleUnitSelect(unit)}
                              disabled={isSelected}
                              className={`w-full p-3 text-left text-sm border-b border-slate-100 last:border-0 ${
                                isSelected
                                  ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                  : 'hover:bg-slate-50'
                              }`}
                            >
                              <span className={`font-medium ${isSelected ? 'text-slate-400' : 'text-slate-800'}`}>
                                {getUnitDisplayName(unit)}
                                {isSelected && ' (ya agregado)'}
                              </span>
                              {unit.type === UnitType.HOUSE && unit.street && (
                                <span className="block text-xs text-slate-500">{unit.street}</span>
                              )}
                            </button>
                          );
                        })
                      ) : (
                        <div className="p-3 text-center text-sm text-slate-500">
                          No se encontraron unidades
                        </div>
                      )}
                    </div>
                  )}

                  {/* Helper text */}
                  {unitSearch.length < 2 && (
                    <p className="mt-2 text-xs text-slate-500">
                      {selectedUnits.length > 0
                        ? 'Busca para agregar más unidades o elimina las existentes'
                        : 'Escribe al menos 2 caracteres para buscar'}
                    </p>
                  )}
                </div>
              </div>
              )}

            </form>

            {canManageHouseholdMembers && (
            <div className="border-t border-slate-100 pt-5">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div>
                  <h4 className="font-bold text-slate-800 flex items-center gap-2">
                    <Users size={16} /> Residentes
                  </h4>
                  <p className="text-xs text-slate-500">
                    {selectedUnits.length > 1
                      ? 'Residentes de la unidad seleccionada abajo.'
                      : 'Residentes vinculados a la unidad.'}
                  </p>
                </div>
              </div>

              {selectedUnits.length > 1 && (
                <div className="flex flex-wrap gap-1.5 mb-3 p-1 bg-slate-100 rounded-lg w-fit max-w-full">
                  {selectedUnits.filter((unit) => unit.id).map((unit) => {
                    const isActive = unit.id === activeMemberUnitId;
                    const memberCount = sortedHouseholdMembers.filter((member) => member.unitId === unit.id).length;
                    return (
                      <button
                        key={unit.id}
                        type="button"
                        onClick={() => handleSwitchMemberUnit(unit.id)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                          isActive
                            ? 'bg-white text-primary-700 shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        <Building2 size={13} className={isActive ? 'text-primary-500' : 'text-slate-400'} />
                        {getUnitDisplayName(unit)}
                        <span className={`text-[10px] rounded-full px-1.5 ${isActive ? 'bg-primary-100 text-primary-700' : 'bg-slate-200 text-slate-500'}`}>
                          {memberCount}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="space-y-2 mb-4">
                {canCreateResident && !editingUserIsPrincipalOfTargetUnit && (
                  <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3">
                    Solo el residente principal de la unidad puede recibir nuevos miembros del hogar.
                  </p>
                )}
                {canCreateResident && editingUserIsPrincipalOfTargetUnit && (
                <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/60 space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-bold text-slate-800">Agregar residente</p>
                      <p className="text-xs text-slate-500">Se enviara una invitacion al correo indicado.</p>
                    </div>
                  </div>

                  {selectedUnits.length > 1 && (
                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">
                        Unidad
                      </label>
                      <select
                        value={newHouseholdMember.unitId}
                        onChange={(event) => setNewHouseholdMember((current) => ({ ...current, unitId: event.target.value }))}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-100"
                      >
                        {selectedUnits.filter((unit) => unit.id).map((unit) => (
                          <option key={unit.id} value={unit.id}>{getUnitDisplayName(unit)}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Input
                      label="Nombre"
                      value={newHouseholdMember.name}
                      onChange={(event) => setNewHouseholdMember((current) => ({ ...current, name: event.target.value }))}
                      placeholder="Nombre completo"
                    />
                    <Input
                      label="Correo"
                      type="email"
                      value={newHouseholdMember.email}
                      onChange={(event) => setNewHouseholdMember((current) => ({ ...current, email: event.target.value }))}
                      placeholder="correo@ejemplo.com"
                    />
                    <Input
                      label="Telefono"
                      value={newHouseholdMember.phone}
                      onChange={(event) => setNewHouseholdMember((current) => ({ ...current, phone: formatPhoneNumber(event.target.value) }))}
                      placeholder="(000) 000-0000"
                    />
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Relacion</label>
                      <select
                        value={newHouseholdMember.relation}
                        onChange={(event) => setNewHouseholdMember((current) => ({ ...current, relation: event.target.value as HouseholdMemberRelation }))}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-100"
                      >
                        {HOUSEHOLD_RELATION_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button
                      type="button"
                      size="sm"
                      variant="primary"
                      icon={<UserPlus size={14} />}
                      isLoading={creatingHouseholdMember}
                      disabled={!selectedUnits.some((unit) => unit.id)}
                      onClick={handleCreateHouseholdMember}
                    >
                      Agregar residente
                    </Button>
                  </div>
                </div>
                )}

                {loadingHousehold ? (
                  <div className="p-4 text-center text-sm text-slate-500">
                    <Loader size={16} className="inline-block animate-spin mr-2" />
                    Cargando residentes...
                  </div>
                ) : visibleHouseholdMembers.length > 0 ? (
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr className="text-[11px] uppercase tracking-wide text-slate-500">
                          <th className="text-left px-3 py-2 font-bold">Residente</th>
                          <th className="text-center px-3 py-2 font-bold">Estatus</th>
                          <th className="text-center px-3 py-2 font-bold">Principal</th>
                          <th className="text-right px-3 py-2 font-bold">Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {visibleHouseholdMembers.map((member) => {
                          const resendCooldown = resendCooldowns[member.id] || 0;

                          return (
                            <tr key={member.id} className={member.isPrincipal ? 'bg-primary-50/60' : 'bg-white'}>
                              <td className="px-3 py-2.5">
                                <p className="font-medium text-slate-800 truncate">{member.name}</p>
                                <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                                  <span className="inline-flex items-center gap-1"><Mail size={12} /> {member.email}</span>
                                  {member.phone && <span className="inline-flex items-center gap-1"><Phone size={12} /> {member.phone}</span>}
                                </div>
                              </td>
                              <td className="px-3 py-2.5 text-center">
                                <span className={`inline-flex text-[10px] px-2 py-0.5 rounded-full border font-bold whitespace-nowrap ${
                                  member.accountStatus === 'ACTIVE'
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                    : 'bg-amber-50 text-amber-700 border-amber-200'
                                }`}>
                                  {member.accountStatus === 'ACTIVE' ? 'Activo' : 'Pendiente'}
                                </span>
                              </td>
                              <td className="px-3 py-2.5 text-center">
                                {member.isPrincipal ? (
                                  <span className="inline-flex items-center gap-1.5 text-xs font-bold text-primary-600 whitespace-nowrap">
                                    <Crown size={16} className="fill-primary-400 text-primary-500" />
                                    Principal
                                  </span>
                                ) : !canManagePrincipal ? (
                                  <span className="text-slate-300" title="No tienes permiso para asignar el residente principal">—</span>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => void handleMakePrincipal(member)}
                                    disabled={transferringPrincipalId !== null}
                                    className="p-1.5 text-slate-300 hover:text-primary-500 hover:bg-primary-50 rounded transition-colors disabled:opacity-50 disabled:pointer-events-none"
                                    title="Hacer residente principal"
                                  >
                                    {transferringPrincipalId === member.id
                                      ? <Loader size={16} className="animate-spin" />
                                      : <Crown size={16} />}
                                  </button>
                                )}
                              </td>
                              <td className="px-3 py-2.5">
                                <div className="flex items-center justify-end gap-1">
                                  {member.email && (
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="secondary"
                                      icon={<Send size={14} />}
                                      disabled={member.accountStatus === 'ACTIVE' || resendCooldown > 0}
                                      isLoading={resendingMemberId === member.id}
                                      onClick={() => handleResendInvitation(member)}
                                    >
                                      {member.accountStatus === 'ACTIVE'
                                        ? 'Cuenta activa'
                                        : resendCooldown > 0
                                          ? `Reenviar (${resendCooldown}s)`
                                          : 'Reenviar'}
                                    </Button>
                                  )}
                                  {canDeleteResident && (
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteHouseholdMember(member)}
                                      className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                      title="Eliminar residente"
                                    >
                                      <Trash2 size={15} />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-4 text-center border border-dashed border-slate-200 rounded-lg text-sm text-slate-500">
                    {selectedUnits.length > 1
                      ? 'No hay residentes vinculados a esta unidad.'
                      : 'No hay residentes vinculados a la unidad.'}
                  </div>
                )}
              </div>

            </div>
            )}
            <div className="flex gap-3 pt-4 border-t border-slate-100">
              {canDeleteResident && editingUser.id && editingUser.status !== 'DELETED' && (
                <Button
                  type="button"
                  variant="danger"
                  icon={<Trash2 size={16}/>}
                  onClick={() => handleDelete(editingUser.id!, editingUser.name || '')}
                >
                  {t('common.delete')}
                </Button>
              )}
              <Button type="button" variant="secondary" className="flex-1" onClick={() => void handleRequestCloseModal()}>
                {t('common.close')}
              </Button>
              <Button type="submit" form="resident-edit-form" variant="primary" className="flex-1" icon={<Save size={16}/>}>
                {t('common.save')}
              </Button>
            </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
